import { kebabCase } from 'lodash-es'
import React, { useState, useCallback } from 'react'
import { Button, Dropdown, Popconfirm, Tooltip } from 'antd'

import MoreOutlined from '@ant-design/icons/MoreOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import SwapOutlined from '@ant-design/icons/SwapOutlined'
import EyeOutlined from '@ant-design/icons/EyeOutlined'
import CheckOutlined from '@ant-design/icons/CheckOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import UndoOutlined from '@ant-design/icons/UndoOutlined'
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined'
import FileImageOutlined from '@ant-design/icons/FileImageOutlined'
import FileOutlined from '@ant-design/icons/FileOutlined'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import relativeTime from 'dayjs/plugin/relativeTime'
import { GQL_CLIENT } from 'src/graphqlClient'
import { deleteFile } from 'src/services/gql/files'
import {
  validateProjectFile,
  rejectProjectFile,
  resetProjectFileStatus,
} from 'src/services/gql/project_files'
import { getFileDownloadUrl } from '@/features/file-upload/services/ProjectFileService'
import { AsyncImage } from '@components/common/AsyncImage'
import type { FileView } from '../types/documents.types'
import {
  USAGE_GRID_COLUMNS,
  USAGE_GRID_COLUMNS_NO_STATUS,
} from './UsageFileTable'

dayjs.extend(relativeTime)
dayjs.locale('fr')

type Props = {
  file: FileView
  editable: boolean
  projectID: number
  onChange: () => void
  isFirst?: boolean
  // When false, the inline "Valider" / "Rejeter" buttons never render —
  // even when the file is in PENDING. The salesman hub passes false so
  // a salesman cannot validate their own upload. Defaults to true to
  // keep the architect-side documents page behavior.
  canSetStatus?: boolean
  // When true, the "Statut" cell is dropped (column hidden table-wide). The
  // row grid must match the header's, so it switches to the no-status grid.
  hideStatus?: boolean
  // Opens the shared preview modal for this file. The modal is owned by the
  // parent table (so it can page through sibling files and validate from the
  // preview), and clicking the thumbnail, the file name, or "Voir" all call
  // this.
  onPreview: () => void
}

type StatusKey = NonNullable<FileView['status']> | 'PENDING'

const STATUS_TAG: Record<
  StatusKey,
  { label: string; text: string; bg: string; border: string; dot: string; dataCy: string }
> = {
  ACCEPTED: {
    label: 'Valide',
    text: '#389e0d',
    bg: '#f6ffed',
    border: '#b7eb8f',
    dot: '#52c41a',
    dataCy: 'file-status-valide',
  },
  REJECTED: {
    label: 'Rejeté',
    text: '#cf1322',
    bg: '#fff1f0',
    border: '#ffccc7',
    dot: '#ff4d4f',
    dataCy: 'file-status-rejete',
  },
  PENDING: {
    label: 'À vérifier',
    text: '#d48806',
    bg: '#fffbe6',
    border: '#ffe58f',
    dot: '#faad14',
    dataCy: 'file-status-a-verifier',
  },
}

const FileStatusTag: React.FC<{ statusKey: StatusKey }> = ({ statusKey }) => {
  const s = STATUS_TAG[statusKey]
  return (
    <span
      data-cy={s.dataCy}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 20,
        padding: '0 8px',
        borderRadius: 10,
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.text,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: s.dot,
          flex: '0 0 auto',
        }}
      />
      {s.label}
    </span>
  )
}

const fileIcon = (mime: string): React.ReactNode => {
  if (mime.startsWith('image/')) return <FileImageOutlined style={{ color: '#1890ff' }} />
  if (mime === 'application/pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
  return <FileOutlined style={{ color: '#8c8c8c' }} />
}

const THUMBNAIL_SIZE = 40

// Inline thumbnail for image files, shown in place of the generic file glyph so
// the architect can eyeball photos during verification without opening each one
// (issue #586). Clicking it opens the same full-size preview modal as the name.
const FileThumbnail: React.FC<{
  fileId: string
  name: string
  onOpen: () => void
}> = ({ fileId, name, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    aria-label={`Aperçu de ${name}`}
    data-cy="file-thumbnail"
    style={{
      flex: '0 0 auto',
      display: 'inline-flex',
      padding: 0,
      border: '1px solid #f0f0f0',
      borderRadius: 4,
      background: '#fafafa',
      cursor: 'pointer',
      lineHeight: 0,
      overflow: 'hidden',
    }}
  >
    <AsyncImage
      cacheKey={`thumbnail-${fileId}`}
      alt={name}
      width={THUMBNAIL_SIZE}
      height={THUMBNAIL_SIZE}
      loader={() =>
        getFileDownloadUrl(GQL_CLIENT, fileId, 'thumbnail').then(
          ({ downloadUrl }) => downloadUrl
        )
      }
      style={{ objectFit: 'cover' }}
    />
  </button>
)

export const UsageFileRow: React.FC<Props> = ({
  file,
  editable,
  projectID,
  onChange,
  isFirst = false,
  canSetStatus = true,
  hideStatus = false,
  onPreview,
}) => {
  const gridColumns = hideStatus
    ? USAGE_GRID_COLUMNS_NO_STATUS
    : USAGE_GRID_COLUMNS
  const [isDeleting, setIsDeleting] = useState(false)
  const [isValidating, setIsValidating] = useState(false)

  const statusKey: StatusKey = file.status ?? 'PENDING'
  // Whoever may decide a file's status may also undo that decision — the two are
  // the same authority, so they share one condition.
  const mayDecide = canSetStatus && editable && file.origin === 'project'
  const canValidate = mayDecide && statusKey === 'PENDING'
  // A settled file can be sent back to review. Only settled ones: PENDING is
  // already the reset state and shows the decision buttons instead.
  const canReset = mayDecide && statusKey !== 'PENDING'

  const onView = onPreview

  const onDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await deleteFile(GQL_CLIENT, file.id)
      onChange()
    } finally {
      setIsDeleting(false)
    }
  }, [file.id, onChange])

  const onAccept = useCallback(async () => {
    setIsValidating(true)
    try {
      await validateProjectFile(GQL_CLIENT, projectID, file.id, undefined)
      onChange()
    } finally {
      setIsValidating(false)
    }
  }, [projectID, file.id, onChange])

  const onReject = useCallback(async () => {
    setIsValidating(true)
    try {
      await rejectProjectFile(GQL_CLIENT, projectID, file.id, undefined)
      onChange()
    } finally {
      setIsValidating(false)
    }
  }, [projectID, file.id, onChange])

  const onReset = useCallback(async () => {
    setIsValidating(true)
    try {
      await resetProjectFileStatus(GQL_CLIENT, projectID, file.id)
      onChange()
    } finally {
      setIsValidating(false)
    }
  }, [projectID, file.id, onChange])

  const onDownload = useCallback(async () => {
    const { downloadUrl, filename } = await getFileDownloadUrl(
      GQL_CLIENT,
      file.id,
      'original'
    )
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename ?? file.name
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [file.id, file.name])

  // Files generated automatically (origin !== 'project') offer only preview +
  // download — no replace/delete/validate/reject.
  const isAutoGenerated = file.origin !== 'project'

  const menuItems = isAutoGenerated
    ? [
        {
          key: 'view',
          label: 'Voir',
          icon: <EyeOutlined />,
          onClick: onView,
        },
        {
          key: 'download',
          label: <span data-cy="action--download-file">Télécharger</span>,
          icon: <DownloadOutlined />,
          onClick: onDownload,
        },
      ]
    : [
        {
          key: 'view',
          label: 'Voir',
          icon: <EyeOutlined />,
          onClick: onView,
        },
        {
          key: 'download',
          label: <span data-cy="action--download-file">Télécharger</span>,
          icon: <DownloadOutlined />,
          onClick: onDownload,
        },
        {
          key: 'replace',
          label: 'Remplacer',
          icon: <SwapOutlined />,
          disabled: true,
        },
        {
          key: 'delete',
          label: (
            <Popconfirm
              title="Supprimer ce fichier ?"
              onConfirm={onDelete}
              okText="Supprimer"
              cancelText="Annuler"
            >
              <span data-cy="action--delete-file">Supprimer</span>
            </Popconfirm>
          ),
          icon: <DeleteOutlined />,
          disabled: !editable || isDeleting,
        },
      ]

  const cellStyle: React.CSSProperties = {
    padding: '6px 8px',
    borderTop: isFirst ? 'none' : '1px solid #f5f5f5',
    fontSize: 12,
    color: '#262626',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  }

  return (
    <div
      data-cy={`file-row--${kebabCase(file.id)}`}
      style={{
        display: 'grid',
        gridTemplateColumns: gridColumns,
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div style={{ ...cellStyle, gap: 8 }}>
        {file.mime_type.startsWith('image/') ? (
          <FileThumbnail fileId={file.id} name={file.name} onOpen={onView} />
        ) : (
          <span style={{ flex: '0 0 auto' }}>{fileIcon(file.mime_type)}</span>
        )}
        <Tooltip title={file.name}>
          <button
            type="button"
            onClick={onView}
            data-cy="action--preview-file"
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              margin: 0,
              cursor: 'pointer',
              color: '#1890ff',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              fontSize: 'inherit',
              fontFamily: 'inherit',
            }}
          >
            {file.name}
          </button>
        </Tooltip>
        {file.comment && file.status === 'REJECTED' && (
          <span style={{ fontSize: 11, color: '#ff4d4f', marginLeft: 6 }}>
            {file.comment}
          </span>
        )}
      </div>
      {!hideStatus && (
        <div style={{ ...cellStyle, gap: 6 }}>
          {canValidate ? (
            <span style={{ display: 'inline-flex', gap: 2 }}>
              <Tooltip title="Valider">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined style={{ color: '#52c41a' }} />}
                  loading={isValidating}
                  onClick={onAccept}
                  data-cy="action--validate-file"
                  style={{ padding: '0 4px' }}
                />
              </Tooltip>
              <Tooltip title="Rejeter">
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined style={{ color: '#ff4d4f' }} />}
                  loading={isValidating}
                  onClick={onReject}
                  data-cy="action--reject-file"
                  style={{ padding: '0 4px' }}
                />
              </Tooltip>
            </span>
          ) : (
            <>
              <FileStatusTag statusKey={statusKey} />
              {canReset && (
                <Tooltip title="Remettre à vérifier">
                  <Button
                    type="text"
                    size="small"
                    icon={<UndoOutlined style={{ color: '#8c8c8c' }} />}
                    loading={isValidating}
                    onClick={onReset}
                    aria-label="Remettre à vérifier"
                    data-cy="action--reset-file-status"
                    style={{ padding: '0 4px' }}
                  />
                </Tooltip>
              )}
            </>
          )}
        </div>
      )}
      <div style={{ ...cellStyle, color: '#8c8c8c', fontSize: 11 }}>
        {dayjs(file.modified_at).fromNow()}
      </div>
      <div style={{ ...cellStyle, justifyContent: 'flex-end' }}>
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button
            size="small"
            icon={<MoreOutlined />}
            data-cy="action--file-menu"
          />
        </Dropdown>
      </div>
    </div>
  )
}
